import { deltaCommQueryParam } from '@nykaa/nes-coupon/components/OfferCommunication/constants';
import { OFFER_ROOT } from 'constants/queryParams';
import { pages } from 'constants/urls';
import { updateDeltaCommStorage } from '@nykaa/nes-coupon/components/OfferCommunication/utils';
import { isValidObject } from '@nykaa/nes-utils/validations';
import { OfferCommDataProps } from '@nykaa/nes-offers/types';

interface QueryParamsObject {
  [key: string]: string;
}
export const queryStringFromParams = (params: object) => {
  if (!params) {
    return '';
  }

  if (typeof params !== 'object' || Array.isArray(params)) {
    throw new Error(`Expected "params" to be an "object" got ${params}`);
  }

  let queryString = '';

  const keys = Object.keys(params);

  if (!(keys.length > 0)) {
    return queryString;
  }

  queryString = '?';
  const queryStringPairs: string[] = [];
  keys.sort().forEach((key: string) => {
    queryStringPairs.push(`${key}=${encodeURIComponent(params[key])}`);
  });

  return `${queryString}${queryStringPairs.join('&')}`;
};

export const queryStringObjectFromUrl = (url: string = '') => {
  const queryString = url.split('?')[1];
  const params = new URLSearchParams(queryString);
  const result: QueryParamsObject = {};
  params.forEach((val, key) => {
    result[key] = val;
  });
  return result;
};

interface UpdatedUrlProps {
  url: string;
  slug?: string;
  parentId?: string;
  childId?: string;
  source?: string;
}

export const getUpdatedUrl = ({
  url,
  slug,
  parentId,
  childId,
  source,
}: UpdatedUrlProps) => {
  if (slug) {
    url = url.replace('{slug}', slug);
  }
  if (parentId) {
    url = url.replace('{id}', parentId);
  }
  if (childId) {
    url = url.replace('{childId}', childId);
  }
  if (source) {
    url = url.replace('{trackingSource}', source);
  }
  return url;
};

export const getUrlWithParamsWithoutHost = (url = '', params) => {
  if (!url) {
    return '';
  }
  try {
    const hostIndex = url.indexOf('.com/');
    if (hostIndex > -1) {
      url = url.substr(hostIndex + 5);
    }
    if (params) {
      const paramSeparator = url.indexOf('?') > -1 ? '&' : '?';
      url += paramSeparator + params;
    }
  } catch (error) {
    throw error;
  }
  return url;
};

export const appendQueryStringToUrl = (
  url: string,
  queryString: string = ''
) => {
  if (!url) {
    throw new Error('"url" can not be empty');
  }

  const queryStringExists = url.indexOf('?') > -1;
  const modifiedUrl = `${url}${queryStringExists ? '&' : '?'}${queryString}`;

  return modifiedUrl;
};

interface RecommendationUrlProps {
  slug: string;
  childId?: string;
  root: string;
  position?: number;
  scroll?: string;
  name?: string;
  totalMedia?: number;
}

export const getRecommendationsUrl = ({
  slug,
  childId,
  root,
  position,
  scroll,
  name,
  totalMedia,
}: RecommendationUrlProps) => {
  const queryString = `root=${root}${childId ? `&skuId=${childId}` : ''}${
    position ? `&pps=${position}` : ''
  }${name ? `&widgetName=${name}` : ''}${
    scroll ? `&scrollType=${scroll}` : ''
  }${totalMedia ? `&sct=${totalMedia}` : ''}`;
  const productUrl = `/${appendQueryStringToUrl(slug, queryString)}`;

  return productUrl;
};

export const getListProductUrl = ({
  slug,
  id,
  parentId,
  position,
  extraParam,
}) => {
  let queryString = `productId=${parentId}`;
  if (parentId !== id) {
    queryString += `&skuId=${id}`;
  }
  if (position) {
    queryString += `&pps=${position}`;
  }
  if (extraParam) {
    queryString += extraParam;
  }
  const productUrl = `/${appendQueryStringToUrl(slug, queryString)}`;
  return productUrl;
};

export const getQueryObjectFromUrl = (url: string) => {
  if (!url || !(url.length > 1) || !(url.indexOf('?') >= 0)) {
    // * > 1 because we expect it to contain a "?"
    throw new Error('"url" cannot be empty');
  }

  const queryObject: { [key: string]: any } = {};

  const queryString = url.split('?')[1];
  const parameters = queryString.split('&');

  parameters.forEach((parameter: string) => {
    const [key, value] = parameter.split('=');

    if (key && value) {
      // TODO: handle case when value is false||0
      queryObject[key] = value;
    }
  });

  return queryObject;
};

export const getOfferListingUrl = (offerId: number, rootParam: string) =>
  `${pages.OFFER_PRODUCTS}/${offerId}?${OFFER_ROOT}=${rootParam}`;

export const checkCartCouponsUrl = (
  previousPageUrl: string,
  onlyCouponsRedirection?: boolean
) => {
  if (onlyCouponsRedirection) {
    return previousPageUrl.includes('coupons');
  }

  return (
    previousPageUrl.includes('coupons') ||
    previousPageUrl.includes('shoppingBag')
  );
};

export const getOfferListingFinalUrl = (
  offerId: number,
  rootParam: string,
  isExperimentEnabled: boolean,
  offer
) => {
  let offerListingUrl = getOfferListingUrl(offerId, rootParam);
  const heading = offer?.title || '';
  const subHeading = offer?.deltaTextModified || offer?.description || '';
  const deltaCommExist = offer?.deltaTextModified ? true : false;
  if (isExperimentEnabled && heading && subHeading) {
    updateDeltaCommStorage({
      heading,
      subHeading,
      offerId: offerId,
      deltaCommExist,
    });
    offerListingUrl += `&${deltaCommQueryParam}=true`;
  }
  return offerListingUrl;
};

export const updateUrlWithDeltaData = (
  url: string,
  offerCommData: OfferCommDataProps
) => {
  updateDeltaCommStorage(offerCommData);
  if (isValidObject(offerCommData)) {
    url += `&${deltaCommQueryParam}=true`;
  }
  return url;
};
